import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldContent,
  Icon,
} from "@design-system/components";
import * as forms from "@design-system/components/forms";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useImpersonation, type ImpersonateCompany } from "@spiriidk/auth";
import { skipToken } from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";

import { useCompanies, useOperators } from "../queries";

interface ImpersonateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getDisplayValue(
  formValue: string,
  items: { id: number; name: string }[],
  placeholder: string
): string {
  if (!formValue) return placeholder;
  return items.find((i) => String(i.id) === formValue)?.name ?? placeholder;
}

interface ImpersonationComboboxFieldProps {
  name: "operator" | "company";
  label: string;
  required?: boolean;
  validationMessage: string;
  options: { id: number; name: string }[];
  displayValue: string;
  isLoading: boolean;
  onSearch: (value: string) => void;
  listeners?: Record<string, unknown>;
  onScrollToBottom?: () => void;
  isLoadingMore?: boolean;
  hasMoreOptions?: boolean;
}

function ImpersonationComboboxField({
  name,
  label,
  required,
  validationMessage,
  options,
  displayValue,
  isLoading,
  onSearch,
  listeners,
  onScrollToBottom,
  isLoadingMore,
  hasMoreOptions,
}: ImpersonationComboboxFieldProps) {
  const { _ } = useLingui();
  const form = forms.useFormContext() as unknown as ReturnType<
    typeof forms.useAppForm
  >;
  const { validationSchemas } = forms;

  return (
    <form.AppField
      name={name}
      validators={{
        onSubmit: validationSchemas.required(validationMessage),
      }}
      listeners={listeners}
    >
      {(field) => (
        <Field>
          <field.Label required={required}>{label}</field.Label>
          <FieldContent>
            <field.Combobox
              onSearch={onSearch}
              isLoading={isLoading}
              onScrollToBottom={onScrollToBottom}
              isLoadingMore={isLoadingMore}
              hasMoreOptions={hasMoreOptions ?? true}
            >
              <field.Combobox.Trigger className="w-full">
                {displayValue}
              </field.Combobox.Trigger>
              <field.Combobox.Content>
                <field.Combobox.Input placeholder={_(msg`Search...`)} />
                <field.Combobox.List>
                  <field.Combobox.Empty>
                    {_(msg`No results found.`)}
                  </field.Combobox.Empty>
                  {options.map((option) => (
                    <field.Combobox.ListItem
                      key={option.id}
                      value={String(option.id)}
                    >
                      {option.name}
                    </field.Combobox.ListItem>
                  ))}
                </field.Combobox.List>
              </field.Combobox.Content>
            </field.Combobox>
            <field.Caption />
          </FieldContent>
        </Field>
      )}
    </form.AppField>
  );
}

function ImpersonateDialog({ open, onOpenChange }: ImpersonateDialogProps) {
  const { _ } = useLingui();
  const {
    operator: currentOperator,
    company: currentCompany,
    applyImpersonation,
    canImpersonateOperator,
    canImpersonateCompany,
  } = useImpersonation();

  const [operatorSearch, setOperatorSearch] = useState("");
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState("");

  const debouncedSetCompanySearch = useDebouncedCallback(
    (value: string) => setDebouncedCompanySearch(value),
    300
  );

  const { operators, isLoading: isLoadingOperators } = useOperators(open);

  const { useAppForm } = forms;

  const form = useAppForm({
    defaultValues: {
      operator: currentOperator ? String(currentOperator.id) : "",
      company: currentCompany ? String(currentCompany.id) : "",
    },
    onSubmit: ({ value }) => {
      const operator = operators.find((op) => String(op.id) === value.operator);
      const company = companies.find((c) => String(c.id) === value.company);

      applyImpersonation(
        operator ? { id: operator.id, name: operator.name } : null,
        company
          ? ({
              id: company.id,
              name: company.name,
            } satisfies ImpersonateCompany)
          : null
      );
    },
  });

  const { useStore: useFormStore } = forms;
  const selectedOperatorValue = useFormStore(
    form.store,
    (s: { values: { operator: string } }) => s.values.operator
  );
  const selectedCompanyValue = useFormStore(
    form.store,
    (s: { values: { company: string } }) => s.values.company
  );

  const operatorIdForQuery = useMemo(() => {
    if (!selectedOperatorValue) return undefined;
    const id = Number(selectedOperatorValue);
    return Number.isNaN(id) ? undefined : id;
  }, [selectedOperatorValue]);

  const {
    companies,
    isLoading: isLoadingCompanies,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCompanies(
    open
      ? {
          operatorId: operatorIdForQuery,
          include: currentCompany?.id,
          search: debouncedCompanySearch || undefined,
        }
      : skipToken
  );

  const filteredOperators = useMemo(() => {
    if (!operatorSearch) return operators;
    const search = operatorSearch.toLowerCase();
    return operators.filter((op) => op.name.toLowerCase().includes(search));
  }, [operators, operatorSearch]);

  useEffect(() => {
    if (!open) {
      setOperatorSearch("");
      setDebouncedCompanySearch("");
      form.reset();
    }
  }, [open, currentOperator, currentCompany, form]);

  const skipCompanyReset = useRef(false);

  const handleOperatorChange = useCallback(() => {
    if (skipCompanyReset.current) return;
    form.setFieldValue("company", "");
  }, [form]);

  const handleCompanyChange = useCallback(
    (value: string) => {
      const company = companies.find((c) => String(c.id) === value);
      if (!company?.operatorId) return;

      skipCompanyReset.current = true;
      form.setFieldValue("operator", String(company.operatorId));
      skipCompanyReset.current = false;
    },
    [companies, form]
  );

  const handleCompanySearch = useCallback(
    (value: string) => {
      debouncedSetCompanySearch(value);
    },
    [debouncedSetCompanySearch]
  );

  const handleCompanyScrollToBottom = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const operatorDisplayValue = getDisplayValue(
    selectedOperatorValue,
    operators,
    _(msg`Select operator`)
  );
  const companyDisplayValue = getDisplayValue(
    selectedCompanyValue,
    companies,
    _(msg`Select customer`)
  );

  const hasChanges =
    selectedOperatorValue !==
      (currentOperator ? String(currentOperator.id) : "") ||
    selectedCompanyValue !== (currentCompany ? String(currentCompany.id) : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{_(msg`Impersonate customer`)}</DialogTitle>
          <DialogDescription className="sr-only">
            {_(
              msg`Select an operator or customer to impersonate their perspective in Connect`
            )}
          </DialogDescription>
        </DialogHeader>

        <Alert semantic="warning">
          <Icon name="alertCircle" />
          <AlertDescription>
            {_(
              msg`While impersonating, you will see and change Connect data from the perspective of the selected operator or customer`
            )}
          </AlertDescription>
        </Alert>

        <form.AppForm>
          <form
            className="flex flex-col gap-8"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className="flex flex-col gap-6">
              {canImpersonateOperator && (
                <ImpersonationComboboxField
                  name="operator"
                  label={_(msg`Operator`)}
                  required
                  validationMessage={_(msg`Operator is required`)}
                  options={filteredOperators}
                  displayValue={operatorDisplayValue}
                  isLoading={isLoadingOperators}
                  onSearch={setOperatorSearch}
                  listeners={{
                    onChange: () => handleOperatorChange(),
                  }}
                />
              )}

              {canImpersonateCompany && (
                <ImpersonationComboboxField
                  name="company"
                  label={_(msg`Customer`)}
                  required
                  validationMessage={_(msg`Customer is required`)}
                  options={companies}
                  displayValue={companyDisplayValue}
                  isLoading={isLoadingCompanies}
                  onSearch={handleCompanySearch}
                  listeners={{
                    onChange: ({ value }: { value: string }) =>
                      handleCompanyChange(value),
                  }}
                  onScrollToBottom={handleCompanyScrollToBottom}
                  isLoadingMore={isFetchingNextPage}
                  hasMoreOptions={hasNextPage ?? false}
                />
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="default"
                onClick={() => onOpenChange(false)}
              >
                {_(msg`Cancel`)}
              </Button>
              <form.SubmitButton variant="emphasised" disabled={!hasChanges}>
                {_(msg`Impersonate`)}
              </form.SubmitButton>
            </DialogFooter>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

export { ImpersonateDialog };
